import React, { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildSocialLoginUrl } from '../services/api';

export const AuthPage: React.FC<{ mode: 'login' | 'signup' }> = ({ mode }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const isSignup = mode === 'signup';
  const returnTo = searchParams.get('returnTo') || '/';
  const authSwitchUrl = `${isSignup ? '/login' : '/signup'}?returnTo=${encodeURIComponent(returnTo)}`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    try {
      if (isSignup) {
        await signup(name, email, password);
      } else {
        await login(email, password);
      }
      navigate(returnTo, { replace: true });
    } catch {
      setError('We could not complete that request. Please check your details and try again.');
    }
  };

  return (
    <main className="mx-auto grid min-h-[calc(100vh-73px)] max-w-7xl items-center px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-md rounded-lg border border-stone-700 bg-stone-800 p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-white">{isSignup ? 'Sign Up' : 'Login'}</h1>
        <p className="mt-2 text-sm text-stone-300">Cinema Zone, Viu Distribution Partner.</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <button className="flex items-center justify-center gap-2 rounded-md border border-stone-600 bg-stone-900 px-4 py-3 font-semibold text-white hover:border-[#ffd460] hover:bg-stone-950" type="button" onClick={() => { window.location.href = buildSocialLoginUrl('google', returnTo); }}>
            <img src="/images/google-logo.svg" alt="" className="h-5 w-5" />
            <span>Google</span>
          </button>
          <button className="flex items-center justify-center gap-2 rounded-md border border-stone-600 bg-stone-900 px-4 py-3 font-semibold text-white hover:border-[#ffd460] hover:bg-stone-950" type="button" onClick={() => { window.location.href = buildSocialLoginUrl('apple', returnTo); }}>
            <img src="/images/apple-logo-white.svg" alt="" className="h-5 w-5" />
            <span>Apple</span>
          </button>
        </div>
        <div className="mt-6 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-stone-400">
          <span className="h-px flex-1 bg-stone-600" />
          Email
          <span className="h-px flex-1 bg-stone-600" />
        </div>
        <form className="mt-6 space-y-4" onSubmit={submit}>
          {isSignup && (
            <label className="block">
              <span className="text-sm font-medium text-stone-300">Name</span>
              <input className="mt-1 w-full rounded-md border border-stone-600 bg-stone-950 px-3 py-2 text-white outline-none focus:border-[#ffd460] focus:ring-2 focus:ring-[#ffd460]/50" type="text" value={name} onChange={(event) => setName(event.target.value)} minLength={2} required />
            </label>
          )}
          <label className="block">
            <span className="text-sm font-medium text-stone-300">Email</span>
            <input className="mt-1 w-full rounded-md border border-stone-600 bg-stone-950 px-3 py-2 text-white outline-none focus:border-[#ffd460] focus:ring-2 focus:ring-[#ffd460]/50" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-stone-300">Password</span>
            <input className="mt-1 w-full rounded-md border border-stone-600 bg-stone-950 px-3 py-2 text-white outline-none focus:border-[#ffd460] focus:ring-2 focus:ring-[#ffd460]/50" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required />
          </label>
          {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <button className="w-full rounded-md bg-[#ffd460] px-4 py-3 font-semibold text-stone-950 hover:bg-[#ffc83d]" type="submit">
            {isSignup ? 'Sign Up' : 'Login'}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-stone-300">
          {isSignup ? 'Already have an account?' : 'New to Cinema Zone?'}{' '}
          <Link className="font-semibold text-white underline decoration-[#ffd460] decoration-2 underline-offset-4" to={authSwitchUrl}>
            {isSignup ? 'Login' : 'Sign Up'}
          </Link>
        </p>
      </div>
    </main>
  );
};
